import useCountdownTimer from '@lib/hooks/useCountDownTimer/useCountdownTimer';
import datetime from '@lib/utils/datetime';
import { inboxLocators } from '@locators/inbox';
import { useContactCenterSettings } from '@modules/FrontEndSettingsModule/hooks/useContactCenterSettings';
import { useFeatureFlags } from '@modules/FeatureFlagModule';
import { Text } from '@servicetitan/anvil2';
import classNames from 'classnames';
import styles from './TaskTimeDisplay.module.css';
import strings from '@components/Task/TaskTimeDisplay/strings';
import { getTestIdProps } from '@lib/testUtils';

/**
 * Threshold in seconds - countdown only displays when remaining time is at or below this value
 */
const COUNTDOWN_THRESHOLD_SECONDS = 5;

interface TaskTimeDisplayProps {
  /**
   * The timestamp (in milliseconds) when the task started, or a Date object
   */
  time: number | Date;
  /**
   * The timestamp (in milliseconds) when the timeout will occur (optional)
   */
  timeoutAt?: number | null;
  /**
   * The type of timeout - determines the label shown (required if timeoutAt is provided)
   */
  timeoutType?: 'agent' | 'queue' | null;
  /**
   * Whether the task is in offered state (countdown only shows for offered tasks)
   */
  isOffered?: boolean;
  /**
   * Whether the call was missed (affects text color)
   */
  isMissed?: boolean;
}

/**
 * Component that displays either a countdown timer (when timeout is imminent) or the call time.
 *
 * When timeout is ≤ threshold seconds away:
 * - Shows warning icon, label (Queue Rollover/Agent Timeout), and countdown
 *
 * Otherwise:
 * - Shows the call time in 12-hour format (e.g., "9:30 AM")
 */
export const TaskTimeDisplay = ({
  time,
  timeoutAt,
  timeoutType,
  isOffered = false,
  isMissed = false,
}: TaskTimeDisplayProps) => {
  // Always call hooks first
  const { count } = useCountdownTimer(timeoutAt ?? 0);
  const contactCenterSettings = useContactCenterSettings();
  const featureFlags = useFeatureFlags();

  // Determine if we should show countdown
  const shouldShowCountdown =
    featureFlags?.['feature-call-timeout-warning'] &&
    contactCenterSettings.callTimeoutWarning?.isEnabled &&
    isOffered &&
    timeoutAt &&
    timeoutType &&
    typeof timeoutAt === 'number' &&
    !Number.isNaN(timeoutAt) &&
    count > 0 &&
    count <= COUNTDOWN_THRESHOLD_SECONDS;

  if (shouldShowCountdown) {
    const label =
      timeoutType === 'queue' ? strings.queueRollover : strings.agentTimeout;

    return (
      <Text
        size='small'
        className={classNames(styles.countdown, 'c-red-500')}
        data-testid={inboxLocators.taskList.timeoutCountdown}
      >
        {label} {strings.countdownFormat(count)}
      </Text>
    );
  }

  // Default: show the time
  return (
    <Text
      size='small'
      subdued
      className={classNames(styles.time, {
        [styles.missed]: isMissed,
      })}
      {...getTestIdProps(inboxLocators.taskList.timeDisplay)}
    >
      {datetime(time).format('h:mm A')}
    </Text>
  );
};
